/**
 * Client-side API for the portal SPA.
 *
 * - `InstallationSettings` + `InstallationSettingsPatchSchema` mirror the
 *   storage package's wire shape. The api Worker enforces its own zod
 *   schema on the server — the client schema is a UX guard (disabling
 *   the Save button when the form is out of range) and the second line
 *   of defence if the server schema changes.
 * - `useSettings` / `useUpdateSettings` are TanStack Query hooks with
 *   optimistic update + rollback on the PATCH error.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { z } from 'zod';

// Mirror of `packages/storage/src/settings.ts` `RISK_THRESHOLD_VALUES`.
// Kept literal-stable so the dropdown options don't change shape if the
// array literal is reordered upstream.
export const RISK_THRESHOLD_VALUES = ['never', 'low', 'medium', 'high', 'critical'] as const;
export const SENSITIVITY_VALUES = ['low', 'medium', 'high'] as const;
/**
 * Mirrors `CODING_AGENT_VALUES` from `@feluda/shared/db`. Adding a new
 * agent is a 3-place change: this constant, the SQL CHECK trigger in
 * `007_coding_agents_and_status_checks.sql`, and the storage helper's
 * `narrowCodingAgents`.
 */
export const CODING_AGENT_VALUES = ['claude_code', 'codex', 'gemini_cli'] as const;

export type RiskThreshold = (typeof RISK_THRESHOLD_VALUES)[number];
export type Sensitivity = (typeof SENSITIVITY_VALUES)[number];
export type CodingAgent = (typeof CODING_AGENT_VALUES)[number];

/** One row of the Status Checks sub-page (id, label, enabled toggle). */
export interface StatusCheck {
  id: string;
  label: string;
  enabled: boolean;
}

export interface InstallationSettings {
  installationId: number;
  updatedAt: number;
  autoReviewOnPush: boolean;
  reviewDrafts: boolean;
  fileChangeLimit: number;
  authorExcludes: string[];
  prSummary: boolean;
  sequenceDiagrams: boolean;
  issueTable: boolean;
  confidenceScore: boolean;
  commentsOutsideDiff: boolean;
  confidenceThreshold: number;
  customInstructions: string;
  commentHeader: string;
  filterLabels: string[];
  filterDisabledLabels: string[];
  filterIncludeAuthors: string[];
  filterExcludeAuthors: string[];
  filterIncludeBranches: string[];
  filterExcludeBranches: string[];
  filterIncludeKeywords: string;
  filterIgnoreKeywords: string;
  autoApprove: boolean;
  autoApproveRiskThreshold: RiskThreshold;
  autoApproveExcludePaths: string[];
  commentSensitivity: Sensitivity;
  nitpickiness: Sensitivity;
  /** Phase 13 — Default Coding Agents sub-page. Always at least one. */
  codingAgents: CodingAgent[];
  /** Phase 13 — single agent the boot path invokes by default. */
  defaultCodingAgent: CodingAgent;
  /** Phase 13 — Status Checks sub-page table. */
  statusChecks: StatusCheck[];
}

/** Patch shape — every field optional. Mirrors `InstallationSettingsPatch` from storage. */
export const InstallationSettingsPatchSchema = z.object({
  autoReviewOnPush: z.boolean().optional(),
  reviewDrafts: z.boolean().optional(),
  fileChangeLimit: z.number().int().min(0).max(10000).optional(),
  authorExcludes: z.array(z.string().min(1).max(128)).max(200).optional(),
  prSummary: z.boolean().optional(),
  sequenceDiagrams: z.boolean().optional(),
  issueTable: z.boolean().optional(),
  confidenceScore: z.boolean().optional(),
  commentsOutsideDiff: z.boolean().optional(),
  confidenceThreshold: z.number().int().min(0).max(100).optional(),
  customInstructions: z.string().max(4096).optional(),
  commentHeader: z.string().max(200).optional(),
  filterLabels: z.array(z.string().min(1).max(128)).max(200).optional(),
  filterDisabledLabels: z.array(z.string().min(1).max(128)).max(200).optional(),
  filterIncludeAuthors: z.array(z.string().min(1).max(128)).max(200).optional(),
  filterExcludeAuthors: z.array(z.string().min(1).max(128)).max(200).optional(),
  filterIncludeBranches: z.array(z.string().min(1).max(128)).max(200).optional(),
  filterExcludeBranches: z.array(z.string().min(1).max(128)).max(200).optional(),
  filterIncludeKeywords: z.string().max(4096).optional(),
  filterIgnoreKeywords: z.string().max(4096).optional(),
  autoApprove: z.boolean().optional(),
  autoApproveRiskThreshold: z.enum(RISK_THRESHOLD_VALUES).optional(),
  autoApproveExcludePaths: z.array(z.string().min(1).max(256)).max(200).optional(),
  commentSensitivity: z.enum(SENSITIVITY_VALUES).optional(),
  nitpickiness: z.enum(SENSITIVITY_VALUES).optional(),
  // Phase 13 — Default Coding Agents sub-page. Storage helper narrows
  // duplicates + unknown values; the api Worker rejects unknown enum
  // values. Mirrors `apps/api/src/portal-routes.ts` Phase-13 schema.
  codingAgents: z.array(z.enum(CODING_AGENT_VALUES)).min(1).max(8).optional(),
  defaultCodingAgent: z.enum(CODING_AGENT_VALUES).optional(),
  // Status Check entries — each must have `id`, `label`, `enabled`.
  // Storage helper drops malformed entries + falls back to defaults;
  // the api Worker rejects a missing field. Mirrored here so the
  // client's dirty-check disables Save on a half-defined row.
  statusChecks: z
    .array(
      z.object({
        id: z.string().min(1).max(64),
        label: z.string().min(1).max(128),
        enabled: z.boolean(),
      }),
    )
    .max(50)
    .optional(),
});

export type InstallationSettingsPatch = z.infer<typeof InstallationSettingsPatchSchema>;

/** One-page navigation scheme for the Settings form. Each section is a
 *  distinct page under `/code-review/<key>` and renders only the rows
 *  owned by that section. The legacy `/settings` composition (all six
 *  sections on one page) is preserved by composing six `<SettingsForm>`s.
 *
 *  Phase 13 added `'agents'` and `'statusChecks'` — the two pages that
 *  used to be `<EmptyState>` placeholders now have real sub-pages. */
export type SettingsSectionKey =
  | 'whenReview'
  | 'summaries'
  | 'instructions'
  | 'comments'
  | 'filters'
  | 'autoApprove'
  | 'agents'
  | 'statusChecks';

interface FetchResponse<T> {
  ok: boolean;
  request_id: string;
  settings: T;
}

async function fetchSettings(installationId: number): Promise<InstallationSettings> {
  const res = await fetch(`/api/portal/settings?installation_id=${installationId}`);
  if (!res.ok) throw new Error(`GET /api/portal/settings → ${res.status}`);
  const body = (await res.json()) as FetchResponse<InstallationSettings>;
  if (!body.ok) throw new Error(`portal API error: ${(body as { error?: string }).error ?? 'unknown'}`);
  return body.settings;
}

async function patchSettings(
  installationId: number,
  patch: InstallationSettingsPatch,
): Promise<InstallationSettings> {
  const res = await fetch(`/api/portal/settings?installation_id=${installationId}`, {
    method: 'PATCH',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(patch),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`PATCH /api/portal/settings → ${res.status}: ${text}`);
  }
  const body = (await res.json()) as FetchResponse<InstallationSettings>;
  return body.settings;
}

const settingsKey = (installationId: number) => ['portal', 'settings', installationId] as const;

export function useSettings(
  installationId: number,
): UseQueryResult<InstallationSettings, Error> {
  return useQuery({
    queryKey: settingsKey(installationId),
    queryFn: () => fetchSettings(installationId),
    enabled: installationId > 0,
  });
}

export function useUpdateSettings(
  installationId: number,
): UseMutationResult<InstallationSettings, Error, InstallationSettingsPatch, { previous: InstallationSettings | undefined }> {
  const queryClient = useQueryClient();
  return useMutation<
    InstallationSettings,
    Error,
    InstallationSettingsPatch,
    { previous: InstallationSettings | undefined }
  >({
    mutationFn: (patch) => patchSettings(installationId, patch),
    onMutate: async (patch) => {
      await queryClient.cancelQueries({ queryKey: settingsKey(installationId) });
      const previous = queryClient.getQueryData<InstallationSettings>(settingsKey(installationId));
      if (previous) {
        queryClient.setQueryData<InstallationSettings>(settingsKey(installationId), {
          ...previous,
          ...patch,
        });
      }
      return { previous };
    },
    onError: (_err, _patch, context) => {
      // Roll back the optimistic update.
      if (context?.previous) {
        queryClient.setQueryData(settingsKey(installationId), context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: settingsKey(installationId) });
    },
  });
}

// ---------------------------------------------------------------------------
// Repositories (Add/Remove Repos page)
// ---------------------------------------------------------------------------

/** Row in the `installation_repositories` table. Mirrors the storage
 *  helper's `InstallationRepositoryView` exactly so the SPA can pass
 *  rows straight from the API into the table without remapping. */
export interface InstallationRepository {
  installationId: number;
  repoId: string;
  enabled: boolean;
  private: boolean;
  defaultBranch: string;
  lastSyncedAt: number;
}

export interface ListRepositoriesResult {
  rows: InstallationRepository[];
  total: number;
  page: number;
  pageSize: number;
}

async function fetchRepositories(
  installationId: number,
  page: number,
  search: string,
): Promise<ListRepositoriesResult> {
  const qs = new URLSearchParams({
    installation_id: String(installationId),
    page: String(page),
    ...(search ? { search } : {}),
  }).toString();
  const res = await fetch(`/api/portal/repositories?${qs}`);
  if (!res.ok) throw new Error(`GET /api/portal/repositories → ${res.status}`);
  const body = (await res.json()) as {
    ok: boolean;
    rows: InstallationRepository[];
    total: number;
    page: number;
    pageSize: number;
    error?: string;
  };
  if (!body.ok) throw new Error(`portal API error: ${body.error ?? 'unknown'}`);
  return {
    rows: body.rows,
    total: body.total,
    page: body.page,
    pageSize: body.pageSize,
  };
}

async function patchRepositoryEnabled(
  installationId: number,
  repoId: string,
  enabled: boolean,
): Promise<void> {
  const [owner, ...rest] = repoId.split('/');
  if (!owner || rest.length === 0) {
    throw new Error(`patchRepositoryEnabled: invalid repoId "${repoId}"`);
  }
  const repo = rest.join('/');
  const qs = `?installation_id=${installationId}`;
  const res = await fetch(
    `/api/portal/repositories/${encodeURIComponent(owner)}/${encodeURIComponent(repo)}${qs}`,
    {
      method: 'PATCH',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ enabled }),
    },
  );
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`PATCH /api/portal/repositories/${owner}/${repo} → ${res.status}: ${text}`);
  }
}

/** TanStack Query key for a single (installation, page, search) page.
 *  We only seed page-1 on PATCH invalidation — see the comment in
 *  `setRepositoryEnabled`. Off-page reads see stale data for up to
 *  60 s, matching the KV cache TTL on the server. */
const reposKey = (installationId: number, page: number, search: string) =>
  ['portal', 'repositories', installationId, page, search] as const;

/** Prefixed form — matches every page/search combo for an
 *  installation, used by the optimistic PATCH mutation. */
const reposKeyPrefix = (installationId: number) =>
  ['portal', 'repositories', installationId] as const;

export function useRepositories(
  installationId: number,
  page: number,
  search: string,
): UseQueryResult<ListRepositoriesResult, Error> {
  return useQuery({
    queryKey: reposKey(installationId, page, search),
    queryFn: () => fetchRepositories(installationId, page, search),
    enabled: installationId > 0,
    placeholderData: (previous) => previous,
  });
}

export function useUpdateRepository(
  installationId: number,
): UseMutationResult<
  void,
  Error,
  { repoId: string; enabled: boolean },
  { previous: Array<[readonly unknown[], ListRepositoriesResult | undefined]> }
> {
  const queryClient = useQueryClient();
  return useMutation<
    void,
    Error,
    { repoId: string; enabled: boolean },
    { previous: Array<[readonly unknown[], ListRepositoriesResult | undefined]> }
  >({
    mutationFn: ({ repoId, enabled }) =>
      patchRepositoryEnabled(installationId, repoId, enabled),
    onMutate: async ({ repoId, enabled }) => {
      // Cancel any in-flight fetches for this installation so the
      // optimistic flip doesn't get clobbered before the PATCH
      // resolves.
      await queryClient.cancelQueries({ queryKey: reposKeyPrefix(installationId) });
      const snapshot = queryClient.getQueriesData<ListRepositoriesResult>({
        queryKey: reposKeyPrefix(installationId),
      });
      for (const [key, value] of snapshot) {
        if (!value) continue;
        queryClient.setQueryData<ListRepositoriesResult>(key, {
          ...value,
          rows: value.rows.map((r) => (r.repoId === repoId ? { ...r, enabled } : r)),
        });
      }
      return { previous: snapshot };
    },
    onError: (_err, _vars, context) => {
      for (const [key, value] of context?.previous ?? []) {
        queryClient.setQueryData(key, value);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: reposKeyPrefix(installationId) });
    },
  });
}